import {
  FormBuilder,
  FormGroup,
  Validators,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import {
  Component,
  Input,
  OnInit,
  OnDestroy,
  ChangeDetectionStrategy,
} from '@angular/core';

import {
  faClock,
  faCalendar,
  faLightbulb,
  faCirclePlay,
  faCircleStop,
} from '@fortawesome/free-regular-svg-icons';
import { faGear, faRefresh } from '@fortawesome/free-solid-svg-icons';
import { Store } from '@ngrx/store';

import {
  Period,
  Dashboard,
  DashboardPage,
  DashboardPresentationSettings,
} from '../../../../core/models/core.models';

import { isFieldInvalid } from 'src/app/shared/utils/form-validation.utils';
import { AppState } from 'src/app/core/state/app.state';
import {
  selectPeriod,
  selectEffectivePeriodForPage,
  selectCompanyDateBounds,
} from '../../../../core/state';

import { Subscription, BehaviorSubject, Observable, take } from 'rxjs';
import { SearchInputComponent } from '../../../../shared/components/search-input/search-input.component';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { NgClass, AsyncPipe, DatePipe } from '@angular/common';
import { SuiTooltipDirective } from '@sui/tooltip';
import { SuiSwitchComponent } from '@sui/switch';
import { NgpLabel } from 'ng-primitives/form-field';
import { SuiLabelDirective } from '@sui/form-field';
import { SuiInputComponent } from '@sui/input';
import { SuiSelectComponent, SuiSelectOptionComponent } from '@sui/select';
import { ColorPickerComponent } from '../../../../shared/components/color-picker/color-picker.component';
import { NgpRovingFocusGroup } from 'ng-primitives/roving-focus';
import { NgpRadioGroup } from 'ng-primitives/radio';
import { SuiRadioGroupDirective, SuiRadioComponent } from '@sui/radio';
import { SuiButtonComponent } from '@sui/button';
import { DateRangeSelectionComponent } from '@sensika/ag-grid-kit';
import { TranslatePipe } from '@ngx-translate/core';

@Component({
  selector: 'app-dashboard-presentation-settings',
  templateUrl: './dashboard-presentation-settings.component.html',
  styleUrl: './dashboard-presentation-settings.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    SearchInputComponent,
    FaIconComponent,
    NgClass,
    SuiTooltipDirective,
    FormsModule,
    ReactiveFormsModule,
    SuiSwitchComponent,
    NgpLabel,
    SuiLabelDirective,
    SuiInputComponent,
    SuiSelectComponent,
    SuiSelectOptionComponent,
    ColorPickerComponent,
    NgpRovingFocusGroup,
    NgpRadioGroup,
    SuiRadioGroupDirective,
    SuiRadioComponent,
    SuiButtonComponent,
    DateRangeSelectionComponent,
    AsyncPipe,
    DatePipe,
    TranslatePipe,
  ],
})
export class DashboardPresentationSettingsComponent
  implements OnInit, OnDestroy
{
  @Input() dashboard: Dashboard;

  public formGroup: FormGroup;
  public pages: DashboardPage[] = [];
  public filteredPages: DashboardPage[] = [];
  public selectedPageId: number | null = null;
  public presentationSettings: DashboardPresentationSettings;
  public minDuration = 5;

  private formSubscription: Subscription;
  private pageValidationState = new Map<number, boolean>();

  // * Period picker state management - store initial values for reset
  private initialPagePeriods = new Map<number, Period | null>();
  public pickerFromDate: Date | null = null;
  public pickerToDate: Date | null = null;
  public companyBounds$: Observable<{ min: Date | null; max: Date }>;

  // ! integration with parent popup component
  public formGroupChange: Observable<FormGroup>;
  public componentDataChange: Observable<any>;
  private formGroupSubject = new BehaviorSubject<FormGroup>(null);
  private componentDataSubject = new BehaviorSubject<any>(null);

  faGear = faGear;
  faClock = faClock;
  faRefresh = faRefresh;
  faCalendar = faCalendar;
  faLightbulb = faLightbulb;
  faCircleStop = faCircleStop;
  faCirclePlay = faCirclePlay;

  isFieldInvalid = isFieldInvalid;

  // * Field groups for validation tracking
  private readonly GENERAL_FIELDS = [
    'duration_seconds',
    'dashboard_title_font_size',
    'page_title_font_size',
    'time_period_font_size',
    'timer_font_size',
    'pagination_font_size',
  ];

  private readonly PAGE_FIELDS = ['page_page_duration_seconds'];

  get hasGeneralInvalidFields(): boolean {
    return this.GENERAL_FIELDS.some((field) =>
      this.isFieldInvalid(this.formGroup, field),
    );
  }

  hasPageInvalidFields(pageId: number): boolean {
    if (this.selectedPageId === pageId) {
      const hasInvalid = this.PAGE_FIELDS.some((field) =>
        this.isFieldInvalid(this.formGroup, field),
      );
      this.pageValidationState.set(pageId, hasInvalid);
      return hasInvalid;
    }

    return this.pageValidationState.get(pageId) ?? false;
  }

  constructor(
    private fb: FormBuilder,
    private store: Store<AppState>,
  ) {
    // ! initialize observables for parent popup integration
    this.formGroupChange = this.formGroupSubject.asObservable();
    this.componentDataChange = this.componentDataSubject.asObservable();
    this.companyBounds$ = this.store.select(selectCompanyDateBounds);
  }

  ngOnInit(): void {
    this.initializeComponent();
  }

  ngOnDestroy(): void {
    if (this.formSubscription) {
      this.formSubscription.unsubscribe();
    }

    // ! cleanup observables
    this.formGroupSubject.complete();
    this.componentDataSubject.complete();
  }

  private initializeComponent(): void {
    // * Deep clone to avoid reference issues (dashboard is already normalized)
    this.pages = structuredClone(this.dashboard.pages || []);
    this.filteredPages = [...this.pages];
    this.presentationSettings = structuredClone(
      this.dashboard.presentation_settings,
    );

    // * Store initial period values for reset functionality
    this.pages.forEach((page) => {
      this.initialPagePeriods.set(page.id, page.period);
    });

    this.initializeForm();
  }

  private initializeForm(): void {
    this.formGroup = this.fb.group({
      // ! Logo visibility
      show_sensika_logo: [this.presentationSettings.show_sensika_logo],
      show_company_logo: [this.presentationSettings.show_company_logo],

      // ! Dashboard Title
      dashboard_title_visible: [
        this.presentationSettings.dashboard_title_visible,
      ],
      dashboard_title_font_size: [
        this.presentationSettings.dashboard_title_font_size,
        [Validators.min(12), Validators.max(42)],
      ],
      dashboard_title_font_weight: [
        this.presentationSettings.dashboard_title_font_weight,
      ],
      dashboard_title_color_light: [
        this.presentationSettings.dashboard_title_color.light,
      ],
      dashboard_title_color_dark: [
        this.presentationSettings.dashboard_title_color.dark,
      ],

      // ! Page Title (global defaults)
      page_title_visible: [this.presentationSettings.page_title_visible],
      page_title_font_size: [
        this.presentationSettings.page_title_font_size,
        [Validators.min(12), Validators.max(42)],
      ],
      page_title_font_weight: [
        this.presentationSettings.page_title_font_weight,
      ],
      page_title_color_light: [
        this.presentationSettings.page_title_color.light,
      ],
      page_title_color_dark: [this.presentationSettings.page_title_color.dark],

      // ! Time Period
      time_period_visible: [this.presentationSettings.time_period_visible],
      time_period_font_size: [
        this.presentationSettings.time_period_font_size,
        [Validators.min(12), Validators.max(42)],
      ],
      time_period_font_weight: [
        this.presentationSettings.time_period_font_weight,
      ],
      time_period_color_light: [
        this.presentationSettings.time_period_color.light,
      ],
      time_period_color_dark: [
        this.presentationSettings.time_period_color.dark,
      ],

      // ! Timers (Auto-Reload & Next Slide)
      timer_font_size: [
        this.presentationSettings.timer_font_size,
        [Validators.min(12), Validators.max(16)],
      ],
      timer_font_weight: [this.presentationSettings.timer_font_weight],
      timer_color_light: [this.presentationSettings.timer_color.light],
      timer_color_dark: [this.presentationSettings.timer_color.dark],

      // ! Pagination
      pagination_visible: [this.presentationSettings.pagination_visible],
      pagination_font_size: [
        this.presentationSettings.pagination_font_size,
        [Validators.min(12), Validators.max(42)],
      ],
      pagination_font_weight: [
        this.presentationSettings.pagination_font_weight,
      ],
      pagination_color_light: [
        this.presentationSettings.pagination_color.light,
      ],
      pagination_color_dark: [this.presentationSettings.pagination_color.dark],

      // ! Presentation Controls
      show_controls: [this.presentationSettings.show_controls],

      // ! Existing global settings
      mode: [this.presentationSettings.mode],
      show_timer: [this.presentationSettings.show_timer],
      duration_seconds: [
        this.presentationSettings.duration_seconds,
        [Validators.min(1)],
      ],

      // ! Individual Page settings (will be populated when page is selected)
      // * Duration (page-level - only remaining page-level customizable property)
      page_page_duration_seconds: [null, [Validators.min(1)]],
    });

    // * subscribe to form changes
    this.formSubscription = this.formGroup.valueChanges.subscribe(() => {
      // * handle form changes
      this.onFormChange();
    });

    // * initialize form with global settings (no page selected by default)
    this.showGlobalSettings();

    // ! emit form group to parent popup for integration
    this.formGroupSubject.next(this.formGroup);
  }

  private onFormChange(): void {
    // * always update data even if form is invalid (for real-time preview)
    // * but mark form as touched for validation display
    const formValue = this.formGroup.value;

    // * track page validation state when page is selected
    if (this.selectedPageId) {
      const hasInvalid = this.PAGE_FIELDS.some((field) =>
        this.isFieldInvalid(this.formGroup, field),
      );
      this.pageValidationState.set(this.selectedPageId, hasInvalid);
    }

    try {
      if (this.selectedPageId) {
        this.updatePageSettings(this.selectedPageId, formValue);
      } else {
        this.updateGlobalSettings(formValue);
      }
    } catch (error) {
      // Silent on purpose (WAP-1857): a synchronous form step, not an API call.
      console.error('Error updating presentation settings:', error);
    }
  }

  private updateGlobalSettings(formValue: any): void {
    // * update presentation settings
    this.presentationSettings = {
      ...this.presentationSettings,
      // ! Logo visibility
      show_sensika_logo: formValue.show_sensika_logo,
      show_company_logo: formValue.show_company_logo,

      // ! Dashboard Title
      dashboard_title_visible: formValue.dashboard_title_visible,
      dashboard_title_font_size: formValue.dashboard_title_font_size,
      dashboard_title_font_weight: formValue.dashboard_title_font_weight,
      dashboard_title_color: {
        light: formValue.dashboard_title_color_light,
        dark: formValue.dashboard_title_color_dark,
      },

      // ! Page Title (global defaults)
      page_title_visible: formValue.page_title_visible,
      page_title_font_size: formValue.page_title_font_size,
      page_title_font_weight: formValue.page_title_font_weight,
      page_title_color: {
        light: formValue.page_title_color_light,
        dark: formValue.page_title_color_dark,
      },

      // ! Time Period
      time_period_visible: formValue.time_period_visible,
      time_period_font_size: formValue.time_period_font_size,
      time_period_font_weight: formValue.time_period_font_weight,
      time_period_color: {
        light: formValue.time_period_color_light,
        dark: formValue.time_period_color_dark,
      },

      // ! Timers
      timer_font_size: formValue.timer_font_size,
      timer_font_weight: formValue.timer_font_weight,
      timer_color: {
        light: formValue.timer_color_light,
        dark: formValue.timer_color_dark,
      },

      // ! Pagination
      pagination_visible: formValue.pagination_visible,
      pagination_font_size: formValue.pagination_font_size,
      pagination_font_weight: formValue.pagination_font_weight,
      pagination_color: {
        light: formValue.pagination_color_light,
        dark: formValue.pagination_color_dark,
      },

      // ! Presentation Controls
      show_controls: formValue.show_controls,

      // ! Existing properties
      mode: formValue.mode,
      show_timer: formValue.show_timer,
      duration_seconds: formValue.duration_seconds,
    };

    // * NEW SMART INHERITANCE: Global changes only affect pages with null values (inheritance)
    // * Pages with custom overrides keep their values until user explicitly applies global settings
    // * The display will automatically show updated inherited values for pages with null settings

    // ! emit data changes to parent popup
    this.emitDataChange(
      'presentation_settings_updated',
      this.presentationSettings,
    );
  }

  private updatePageSettings(pageId: number, formValue: any): void {
    const pageIndex = this.pages.findIndex((p) => p.id === pageId);

    if (pageIndex === -1) return;

    // * update page with duration value (only remaining page-level property)
    this.pages = this.pages.map((page, i) => {
      if (i !== pageIndex) return page;

      return {
        ...page,
        duration_seconds: formValue.page_page_duration_seconds,
      };
    });

    // ! emit data changes to parent popup
    this.emitDataChange('page_settings_updated', {
      pageId,
      page: this.pages[pageIndex],
    });
  }

  public selectGeneral(): void {
    this.selectedPageId = null;
    this.showGlobalSettings();
  }

  public selectPage(pageId: number): void {
    this.selectedPageId = pageId;
    this.showPageSettings(pageId);
  }

  public isPageSelected(pageId: number): boolean {
    return this.selectedPageId === pageId;
  }

  private showGlobalSettings(): void {
    // * populate form with global presentation settings
    this.formGroup.patchValue({
      // * Logo visibility
      show_sensika_logo: this.presentationSettings.show_sensika_logo,
      show_company_logo: this.presentationSettings.show_company_logo,

      // * Dashboard Title
      dashboard_title_visible:
        this.presentationSettings.dashboard_title_visible,
      dashboard_title_font_size:
        this.presentationSettings.dashboard_title_font_size,
      dashboard_title_font_weight:
        this.presentationSettings.dashboard_title_font_weight,
      dashboard_title_color_light:
        this.presentationSettings.dashboard_title_color.light,
      dashboard_title_color_dark:
        this.presentationSettings.dashboard_title_color.dark,

      // * Page Title (global defaults)
      page_title_visible: this.presentationSettings.page_title_visible,
      page_title_font_size: this.presentationSettings.page_title_font_size,
      page_title_font_weight: this.presentationSettings.page_title_font_weight,
      page_title_color_light: this.presentationSettings.page_title_color.light,
      page_title_color_dark: this.presentationSettings.page_title_color.dark,

      // * Time Period
      time_period_visible: this.presentationSettings.time_period_visible,
      time_period_font_size: this.presentationSettings.time_period_font_size,
      time_period_font_weight:
        this.presentationSettings.time_period_font_weight,
      time_period_color_light:
        this.presentationSettings.time_period_color.light,
      time_period_color_dark: this.presentationSettings.time_period_color.dark,

      // * Timers
      timer_font_size: this.presentationSettings.timer_font_size,
      timer_font_weight: this.presentationSettings.timer_font_weight,
      timer_color_light: this.presentationSettings.timer_color.light,
      timer_color_dark: this.presentationSettings.timer_color.dark,

      // * Pagination
      pagination_visible: this.presentationSettings.pagination_visible,
      pagination_font_size: this.presentationSettings.pagination_font_size,
      pagination_font_weight: this.presentationSettings.pagination_font_weight,
      pagination_color_light: this.presentationSettings.pagination_color.light,
      pagination_color_dark: this.presentationSettings.pagination_color.dark,

      // * Presentation Controls
      show_controls: this.presentationSettings.show_controls,

      // * Existing properties
      mode: this.presentationSettings.mode,
      show_timer: this.presentationSettings.show_timer,
      duration_seconds: this.presentationSettings.duration_seconds,

      // * clear page-specific value
      page_page_duration_seconds: null,
    });
  }

  private showPageSettings(pageId: number): void {
    // * find the selected page
    const selectedPage = this.pages.find((p) => p.id === pageId);
    if (!selectedPage) return;

    // * Get effective duration (with inheritance from global)
    const effectiveDurationSeconds =
      selectedPage.duration_seconds ||
      this.presentationSettings.duration_seconds;

    // * Only patch the page-specific control (general controls already set from init)
    this.formGroup.patchValue({
      page_page_duration_seconds: effectiveDurationSeconds,
    });

    // * Initialize period picker with current or pending value
    this.initializePeriodPicker(pageId);
  }

  // * Helper methods for template
  public trackByPageId(index: number, page: DashboardPage): number {
    return page.id;
  }

  public getEffectivePeriodForPage(pageId: number): Observable<any> {
    return this.store.select(selectEffectivePeriodForPage(pageId));
  }

  public getDashboardOnlyPeriod(): Observable<Period> {
    return this.store.select(selectPeriod);
  }

  public shouldShowPageTimer(page: DashboardPage): boolean {
    // * show timer if page has custom duration or global timer is enabled
    return this.presentationSettings?.show_timer || false;
  }

  public getSelectedPageName(): string {
    if (!this.selectedPageId) return '';
    const selectedPage = this.pages.find((p) => p.id === this.selectedPageId);
    return selectedPage?.name || '';
  }

  // ! public method to get the current presentation data for parent popup
  public getPresentationData(): {
    presentation_settings: DashboardPresentationSettings;
    pages: DashboardPage[];
  } {
    return {
      presentation_settings: this.presentationSettings,
      pages: this.pages,
    };
  }

  // ! public method to create form group (called by parent popup if needed)
  public createFormGroup(): void {
    // * form group is already created in initializeForm
    this.formGroupSubject.next(this.formGroup);
  }

  // ! helper method to emit data changes to parent popup
  private emitDataChange(type: string, data: any): void {
    this.componentDataSubject.next({
      type,
      data,
      timestamp: Date.now(),
    });
  }

  // * get display value for page list (showing actual values)
  public getDisplayDuration(page: DashboardPage): number {
    return page.duration_seconds;
  }

  // ! === DURATION "APPLY TO ALL" METHOD ===
  // * Apply global duration to all pages
  public applyDurationToAllPages(): void {
    this.formGroup.markAsDirty();
    const globalDuration = this.presentationSettings.duration_seconds;

    this.pages = this.pages.map((page) => ({
      ...page,
      duration_seconds: globalDuration,
    }));

    // Refresh form if on page view
    if (this.selectedPageId) {
      this.showPageSettings(this.selectedPageId);
    }

    this.emitDataChange('apply_duration_to_all', {
      message: 'Duration applied to all pages',
    });
  }

  // * Reset page duration to global value
  public resetPageFieldToGlobal(field: keyof DashboardPage): void {
    if (!this.selectedPageId) return;

    const pageIndex = this.pages.findIndex((p) => p.id === this.selectedPageId);
    if (pageIndex === -1) return;

    this.formGroup.markAsDirty();

    // * Set duration to current global value
    let globalValue;
    if (field === 'duration_seconds') {
      globalValue = this.presentationSettings.duration_seconds;
    }

    this.pages = this.pages.map((page, i) =>
      i === pageIndex ? { ...page, [field]: globalValue } : page,
    );

    // * refresh form with updated values
    this.showPageSettings(this.selectedPageId);

    this.emitDataChange('reset_page_field_to_global', {
      pageId: this.selectedPageId,
      field,
      new_value: globalValue,
    });
  }

  public onSearchChange(event: string): void {
    const searchTerm = event.toLowerCase().trim();

    this.filteredPages = this.pages.filter(
      (page) =>
        page.name.toLowerCase().includes(searchTerm) ||
        page.description?.toLowerCase().includes(searchTerm),
    );
  }

  // * === PERIOD PICKER METHODS ===

  // * Initialize period picker with current page period
  private initializePeriodPicker(pageId: number): void {
    const page = this.pages.find((p) => p.id === pageId);
    if (!page) return;

    const effectivePeriod = page.period || this.getDashboardPeriod();
    this.pickerFromDate = new Date(effectivePeriod.startTimestamp * 1000);
    this.pickerToDate = new Date(effectivePeriod.endTimestamp * 1000);
  }

  // * Get dashboard period for reference
  private getDashboardPeriod(): Period {
    let dashboardPeriod: Period | null = null;

    this.store
      .select(selectPeriod)
      .pipe(take(1))
      .subscribe((period) => {
        dashboardPeriod = period;
      });

    return dashboardPeriod;
  }

  // * Handle date range selection from picker - updates immediately
  public onPeriodRangeSelection(range: { from: Date; to: Date }): void {
    if (!this.selectedPageId) return;

    this.pickerFromDate = range.from;
    this.pickerToDate = range.to;

    // * update Page period on select (immediately)
    const period: Period = {
      startTimestamp: Math.floor(this.pickerFromDate.getTime() / 1000),
      endTimestamp: Math.floor(this.pickerToDate.getTime() / 1000),
    };

    this.updatePagePeriod(this.selectedPageId, period);
  }

  // * Set period to null (inherit from dashboard) - updates immediately
  public useDashboardDefaultPeriod(): void {
    if (!this.selectedPageId) return;

    this.updatePagePeriod(this.selectedPageId, null);

    const dashboardPeriod = this.getDashboardPeriod();
    this.pickerFromDate = new Date(dashboardPeriod.startTimestamp * 1000);
    this.pickerToDate = new Date(dashboardPeriod.endTimestamp * 1000);
  }

  // * Reset period to initial value (from when dialog opened)
  public resetPeriodToInitial(): void {
    if (!this.selectedPageId) return;

    const initialPeriod = this.initialPagePeriods.get(this.selectedPageId);
    this.updatePagePeriod(this.selectedPageId, initialPeriod);

    this.initializePeriodPicker(this.selectedPageId);
  }

  // * Helper to update page period and mark form dirty
  private updatePagePeriod(pageId: number, period: Period | null): void {
    const pageIndex = this.pages.findIndex((p) => p.id === pageId);
    if (pageIndex === -1) return;

    this.pages = this.pages.map((page, i) =>
      i === pageIndex ? { ...page, period: period } : page,
    );

    this.formGroup.markAsDirty();

    this.emitDataChange('page_period_updated', {
      pageId: pageId,
      period: period,
    });
  }

  public get isUseDashboardDefaultDisabled(): boolean {
    if (!this.selectedPageId) return true;

    const page = this.pages.find((p) => p.id === this.selectedPageId);
    return !page || page.period === null;
  }

  public get isRevertChangesDisabled(): boolean {
    if (!this.selectedPageId) return true;

    const page = this.pages.find((p) => p.id === this.selectedPageId);
    if (!page) return true;

    const initialPeriod = this.initialPagePeriods.get(this.selectedPageId);

    // * Both null - no changes
    if (page.period === null && initialPeriod === null) return true;

    // * One is null, other isn't - changed
    if (page.period === null || initialPeriod === null) return false;

    // * Both have values - compare timestamps
    return (
      page.period.startTimestamp === initialPeriod.startTimestamp &&
      page.period.endTimestamp === initialPeriod.endTimestamp
    );
  }

  onVisibilityIconClick(page: DashboardPage, event: Event): void {
    event.stopPropagation(); // ! prevent Page selection

    const updatedVisibility = !page.is_visible_in_presentation;

    // * оptimistically update local State
    const pageIndex = this.pages.findIndex((p) => p.id === page.id);

    if (pageIndex !== -1) {
      this.pages = this.pages.map((page, i) => {
        if (i !== pageIndex) return page;

        return {
          ...page,
          is_visible_in_presentation: updatedVisibility,
        };
      });

      // * update filteredPages as well
      this.filteredPages = this.filteredPages.map((page, i) => {
        if (page.id !== this.pages[pageIndex].id) return page;

        return {
          ...page,
          is_visible_in_presentation: updatedVisibility,
        };
      });
    }

    this.formGroup.markAsDirty();

    this.emitDataChange('page_visibility_toggled', {
      pageId: page.id,
      visible: updatedVisibility,
    });
  }
}
